import { useState, useCallback } from 'react'
import {
  quizQuestions,
  calculateResult,
  type ResultProfile,
} from '../data/quizData'
import { useAnalytics } from './useAnalytics'

export type QuizStep = 'intro' | 'question' | 'loading' | 'result' | 'cta'

interface QuizState {
  step: QuizStep
  questionIndex: number
  selectedAnswers: Record<number, string>
  collectedTags: string[]
  result: ResultProfile | null
}

export function useQuiz() {
  const [state, setState] = useState<QuizState>({
    step: 'intro',
    questionIndex: 0,
    selectedAnswers: {},
    collectedTags: [],
    result: null,
  })

  const { startSession, track, complete, clickCTA } = useAnalytics()

  const totalQuestions = quizQuestions.length
  const currentQuestion = quizQuestions[state.questionIndex] || null
  const progress =
    state.step === 'question'
      ? (state.questionIndex / totalQuestions) * 100
      : state.step === 'loading' ||
          state.step === 'result' ||
          state.step === 'cta'
        ? 100
        : 0

  const startQuiz = useCallback(() => {
    startSession()
    track('quiz_start')
    setState((prev) => ({ ...prev, step: 'question', questionIndex: 0 }))
  }, [startSession, track])

  const answerQuestion = useCallback(
    (questionId: number, tag: string) => {
      track(`question_${questionId}`, { questionId, tag })

      setState((prev) => {
        const newAnswers = { ...prev.selectedAnswers, [questionId]: tag }
        const newTags = [...prev.collectedTags, tag]
        const nextIndex = prev.questionIndex + 1

        if (nextIndex >= totalQuestions) {
          const result = calculateResult(newTags)
          return {
            ...prev,
            selectedAnswers: newAnswers,
            collectedTags: newTags,
            step: 'loading' as QuizStep,
            result,
          }
        }

        return {
          ...prev,
          selectedAnswers: newAnswers,
          collectedTags: newTags,
          questionIndex: nextIndex,
        }
      })
    },
    [totalQuestions, track],
  )

  const goToResult = useCallback(() => {
    setState((prev) => {
      if (prev.result) {
        complete(prev.result.id)
      }
      return { ...prev, step: 'result' }
    })
  }, [complete])

  const goToCTA = useCallback(() => {
    clickCTA()
    setState((prev) => ({ ...prev, step: 'cta' }))
  }, [clickCTA])

  const restart = useCallback(() => {
    setState({
      step: 'intro',
      questionIndex: 0,
      selectedAnswers: {},
      collectedTags: [],
      result: null,
    })
  }, [])

  return {
    step: state.step,
    questionIndex: state.questionIndex,
    currentQuestion,
    totalQuestions,
    progress,
    result: state.result,
    collectedTags: state.collectedTags,
    startQuiz,
    answerQuestion,
    goToResult,
    goToCTA,
    restart,
  }
}
